// /login/phone — add or change the verified phone on the SIGNED-IN account.
//
// Distinct from "Continue with phone" on the sign-in form, which signs in AS a number (and would
// create a second account for someone already signed in by email). Here the account comes from the
// SSO session; the number is attached to it. Afterwards the user goes back to return_to (Forge's
// /auth/login?next=…), and that fresh sign-in carries the phone claims.
import {useState, useEffect} from "react";
import {ApiError, resendAccountPhone, startAccountPhone, verifyAccountPhone} from "./api";
import {getReturnTo} from "./storage";
import {activeDeployment} from "../../shared/deployment-config.ts";
import {ContactField, contactE164, initialContact, type ContactValue} from "@twoshot/ui-kit/phone/react";
import {formatE164} from "@twoshot/ui-kit/phone";
import {flagUrl} from "./flags";
import {useHumanCheck} from "./humanCheck";

type Phase = "number" | "code" | "done";

export function phonePageErrorCopy(err: unknown): string {
    if (!(err instanceof ApiError)) return "Network error. Please try again.";
    switch (err.code) {
        case "invalid_phone": return "Enter your number with its country code, e.g. +44 7700 900123.";
        case "unsupported_country": return "Phone verification isn't available for that country yet.";
        case "voip_blocked": return "That looks like an internet (VoIP) number. Please use a mobile number.";
        case "unsupported_line_type": return "Please use a mobile number.";
        case "phone_in_use": return "That number is already on another account.";
        case "phone_opted_out": return "This number has opted out of our texts (it replied STOP). Reply START to our last text, then try again.";
        case "wrong_code": return "That code isn't right. Check it and try again.";
        case "expired": return "That code has expired. Request a new one.";
        case "verification_unavailable": return "We couldn't check your code just now. Try the same code again in a moment.";
        case "too_many_attempts": return "Too many attempts. Request a new code.";
        case "too_soon": return "Please wait for the resend countdown before requesting another code.";
        case "call_unavailable": return "Call verification is not available for this number. Use the text code instead.";
        case "delivery_limit": return "This code cannot be resent again. Start again for a new code.";
        case "human_check_required":
        case "human_check_failed": return "The browser check didn't pass. Please try again.";
        case "human_check_unavailable": return "The browser check couldn't load. Please try again.";
        case "use_email_instead": return "We can't verify this number. Please use another mobile number.";
        case "phone_check_unavailable": return "Phone verification isn't available for this number right now.";
        default:
            if (err.code.startsWith("too_many")) return "Too many codes requested. Try again later.";
            return "Something went wrong. Please try again.";
    }
}

function goSignIn() {
    const here = window.location.pathname + window.location.search;
    window.location.assign(`/login/?return_to=${encodeURIComponent(here)}`);
}

export function PhoneVerifyPage() {
    const cfg = activeDeployment();
    const [phase, setPhase] = useState<Phase>("number");
    const [contact, setContact] = useState<ContactValue>(() => initialContact("phone"));
    const [phone, setPhone] = useState("");
    const [code, setCode] = useState("");
    const [verificationId, setVerificationId] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [resendAt,setResendAt]=useState(0);
    const [clock,setClock]=useState(Date.now);
    useEffect(()=>{const timer=setInterval(()=>setClock(Date.now()),1000);return()=>clearInterval(timer);},[]);
    const cooldown=Math.max(0,Math.ceil((resendAt-clock)/1000));
    const [notice, setNotice] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const human = useHumanCheck();

    async function run(fn: () => Promise<void>) {
        setBusy(true); setError(null); setNotice(null);
        try { await fn(); }
        catch (err) {
            if (err instanceof ApiError && err.status === 401) { goSignIn(); return; }
            if(err instanceof ApiError && err.detail && typeof err.detail==="object") {const delay=(err.detail as {retry_after_seconds?:unknown}).retry_after_seconds;if(typeof delay==="number"&&delay>0)setResendAt(Date.now()+delay*1000);}
            setError(phonePageErrorCopy(err));
        }
        finally { setBusy(false); }
    }

    if (!cfg.phone.enabled) {
        return <div className="auth-container"><div className="head"><h1>Phone verification</h1></div><p className="subtitle">Phone verification isn't available here.</p></div>;
    }

    if (phase === "done") {
        return (
            <div className="auth-container">
                <div className="head"><h1>Phone verified</h1></div>
                <p className="subtitle">Taking you back…</p>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <form noValidate onSubmit={(e) => {
                e.preventDefault();
                if (phase === "number") {
                    const e164 = contactE164(contact);
                    if (!e164) { setError("Enter your mobile number."); return; }
                    void run(async () => {
                        const r = await human.guard(token => startAccountPhone(e164, token));
                        setPhone(e164);
                        setVerificationId(r.verification_id);
                        setResendAt(Date.now()+r.retry_after_seconds*1000);
                        setPhase("code");
                    });
                } else if (verificationId) {
                    void run(async () => {
                        await verifyAccountPhone(verificationId, code.trim());
                        setPhase("done");
                        window.setTimeout(() => window.location.assign(getReturnTo().url), 800);
                    });
                }
            }}>
                <div className="head"><h1>Verify your phone</h1></div>
                {phase === "code" && <p className="sent">We texted a code to <strong>{formatE164(phone)}</strong></p>}
                <div className="stack">
                    {phase === "number" ? (
                        <ContactField accept="phone" value={contact} onChange={(v) => { setContact(v); if (error) setError(null); }}
                                      id="phone-number" placeholder="Mobile number" autoFocus invalid={!!error} flagUrl={flagUrl}/>
                    ) : (
                        <input id="phone-code" className="code-input" type="text" autoComplete="one-time-code" inputMode="numeric" autoFocus
                               aria-label="Code" placeholder="000000" maxLength={8} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}/>
                    )}
                    {phase === "number" && human.slot}
                    {error && <div className="error-msg" role="alert">{error}</div>}
                    {notice && <p className="hint" role="status">{notice}</p>}
                    <button type="submit" className="btn btn-primary"
                            disabled={busy || (phase === "code" && code.trim().length < 4)}>
                        {busy ? <span className="spinner"/> : phase === "number" ? "Text me" : "Verify"}
                    </button>
                </div>
                {phase === "code" && verificationId && (
                    <div className="links">
                        <button type="button" className="btn btn-ghost btn-md" disabled={busy || cooldown > 0} onClick={() => void run(async () => {
                            const sent = await resendAccountPhone(verificationId);
                            setResendAt(Date.now() + sent.retry_after_seconds * 1000);
                            setNotice("We sent the same code again.");
                        })}>{cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}</button>
                        <button type="button" className="btn btn-ghost btn-md" disabled={busy} onClick={() => {
                            setPhase("number"); setCode(""); setVerificationId(null); setError(null); setNotice(null);
                        }}>Use a different number</button>
                    </div>
                )}
            </form>
        </div>
    );
}
